import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ContentChild, ElementRef, HostListener, effect, forwardRef, input, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TransformationService } from '../../../services/TransformationService';

@Component({
  selector: 'app-editable-datetime-control',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './editable-datetime-control.component.html',
  styleUrls: ['./editable-datetime-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EditableDatetimeControlComponent),
      multi: true
    }
  ]
})
export class EditableDatetimeControlComponent implements ControlValueAccessor {
  readonly minHeight = input<number>(24);
  readonly editMode = input<boolean>(false);
  readonly allowIndividualEditMode = input<boolean>(false);
  readonly dateFormat = input<'date' | 'datetime-local' | 'time'>('datetime-local');
  readonly locale = input<string>('en-US');
  readonly timeZoneIndicator = input<boolean>(false);
  readonly externalValue = input<any>(null);

  @ContentChild('editInput', { static: false}) editInputRef?: ElementRef;

  public isEditMode =false;
  public showEditButton: boolean = false;
  
  private _value: any = null;
  private _originalValue: any = null;
  private _blockChangeNotification: boolean = false;

  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(private elementRef: ElementRef, private transformationService:TransformationService, private cd:ChangeDetectorRef) {
    effect(() => {
        if(this.editMode() && !this.isEditMode){
            this.enterEditMode();
        } 
        else if(!this.editMode() && this.isEditMode) this.exitEditMode(false);
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.allowIndividualEditMode() && this.isEditMode && !this.elementRef.nativeElement.contains(event.target)) {
      this.exitEditMode(true);
    }
  }

  get disabled(): boolean {
    return this.editInputRef?.nativeElement?.disabled ?? false;
  }

  get value(): any {
    return this._value;
  }

  set value(val: any) {
    const normalizedVal = val ?? null;
    if (this._value !== normalizedVal) {
      this._value = normalizedVal;
      if (!this._blockChangeNotification) {
        this.onChange(normalizedVal);
      }
    }
  }

  get displayValue(): string {
    const val = this.externalValue() ?? this._value;
    if (!val) return '';
    
    try {
      const date = val instanceof Date ? val : new Date(val);

      if (isNaN(date.getTime())) return '';

      const formatType = this.dateFormat();
      
      if (formatType === 'date') {
        return date.toLocaleDateString(this.locale());
      } else if (formatType === 'time') {
        return date.toLocaleTimeString(this.locale()) + (this.timeZoneIndicator() ? (" " + this.transformationService.getTimeZone(date)): '');
      } else {
        // datetime-local
        return date.toLocaleString(this.locale()) + (this.timeZoneIndicator() ? (" " + this.transformationService.getTimeZone(date)): '');
      }
    } catch (e) {
      return '';
    }
  }

  public toggleEditButton(value: boolean): void {
    this.showEditButton = value;
  }

  public enterEditMode(): void {
    // Capture the original value before entering edit mode
    if (this._value instanceof Date) {
      this._originalValue = new Date(this._value);
    } else if (this._value) {
      try {
        this._originalValue = new Date(this._value);
      } catch (e) {
        this._originalValue = null;
      }
    } else {
      this._originalValue = null;
    }
    
    this._blockChangeNotification = true;
    setTimeout(() => {
      this.isEditMode=true;
      
      if(this.allowIndividualEditMode()) {
        setTimeout(() => {
          const inputEl = this.elementRef.nativeElement.querySelector('input');
          if (inputEl) {
            inputEl.focus();
          }
          this._blockChangeNotification = false;
        }, 0);
      } else {
        setTimeout(() => {
          this._blockChangeNotification = false;
        }, 0);
      }
    }, 0);
  }

  public exitEditMode(save: boolean): void {
    this._blockChangeNotification = true;
    if (!save) {
      // Properly compare Date objects by timestamp
      const currentTime = this._value instanceof Date ? this._value.getTime() : (this._value ? new Date(this._value).getTime() : null);
      const originalTime = this._originalValue instanceof Date ? this._originalValue.getTime() : (this._originalValue ? new Date(this._originalValue).getTime() : null);
      const valueChanged = currentTime !== originalTime;
      
      this._value = this._originalValue;
      
      if (valueChanged) {
        this.onChange(this._value);
      }
    }
    
    setTimeout(() => {
      this.isEditMode=false;
      setTimeout(() => {
        this._blockChangeNotification = false;
      }, 0);
    }, 0);
  }

  public onKeyDown(event: KeyboardEvent): void {
    if(this.allowIndividualEditMode()) {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.exitEditMode(true);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.exitEditMode(false);
      }
    }
  }

  public openCalendar(): void {
    const inputEl = this.elementRef.nativeElement.querySelector('input[type="date"], input[type="datetime-local"], input[type="time"]');
    if (inputEl) {
      inputEl.showPicker?.();
    }
  }

  // ControlValueAccessor methods
  public writeValue(value: any): void {
    if (value === null || value === undefined) {
        this._value = null;
    } else if (value instanceof Date) {
        this._value = value;
    } else if (typeof value === 'string') {
        try {
            // date-only string e.g. "2024-03-01" — parse as local to avoid UTC shift
            if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                const [year, month, day] = value.split('-').map(Number);
                this._value = new Date(year, month - 1, day); // local midnight, no UTC shift
            } else {
                const date = new Date(value);
                this._value = isNaN(date.getTime()) ? null : date;
            }
        } catch (e) {
            this._value = null;
        }
    } else {
        try {
            const date = new Date(value);
            this._value = isNaN(date.getTime()) ? null : date;
        } catch (e) {
            this._value = null;
        }
    }
    this.cd.markForCheck();
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

}